import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    OnInit,
    ViewChild,
    inject
} from "@angular/core";

import {
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators
} from "@angular/forms";

interface IUpdatePasswordFormGroup {
    currentPassword: FormControl<string>;
    newPassword: FormControl<string>;
}

import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import {
    MatDialogActions,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle
} from "@angular/material/dialog";

import {
    MatError,
    MatFormField,
    MatLabel,
    MatSuffix
} from "@angular/material/form-field";

import { MatButton } from "@angular/material/button";
import { MatInput } from "@angular/material/input";

import { COMMON_APP_SERVICE } from "../../../application/constants/app-tokens";
import { IAppService } from "../../../application/interfaces/app-service";
import { ICommonConfig } from "../../../application/interfaces/common-config";
import { ButtonDirective } from "../../../application/directives/button/button.directive";
import { DialogCloseButtonDirective } from "../../../application/directives/button/dialog-close-button.directive";
import { DialogContentContainerDirective } from "../../../directives/dialog-content-container/dialog-content-container.directive";
import { EnhancedFormDirective } from "../../../forms/enhanced-form.directive";
import { ISubmitFormDialog } from "../../../forms/interfaces/submit-form-dialog";
import { IAuthenticationAppRouterService } from "../../interfaces/authentication-app-router-service";
import { UserRequestService } from "../../services/request/user/user-request.service";
import { SetPasswordControlComponent } from "../../shared/set-password-control/set-password-control.component";
import { BadRequestException } from "../../../exceptions/bad-request-exception";
import { CommonValidators } from "../../../custom-validators/common-validators";
import { ErrorMessageComponent } from "../../../components/error-message/error-message.component";
import { EnhancedControlDirective } from "../../../forms/directives/form-control/enhanced-control.directive";
import { ClearInputDirective } from "../../../forms/directives/clear-input/clear-input.directive";
import { ClearButtonComponent } from "../../../components/shared/clear-button/clear-button.component";
import { RxjsUtil } from "../../../utils/rxjs-util";

/**
 * Dialog submit form component for updating the current user's password.
 *
 * Renders a dialog form with two password fields: one for the current password
 * and one for the new password. The new password field is disabled until the user
 * enters a value in the current password field.
 *
 * On submit, calls `UserRequestService.setPassword$` with the new and current
 * password values. Displays a success notification on success, or an error
 * notification on failure. If the current password is invalid (400 response),
 * an inline field error is shown on the `currentPassword` control — it auto-clears
 * when the user changes the field value.
 *
 * Implements {@link ISubmitFormDialog} to support unsaved-changes detection
 * via the `checkChangesCanDeactivate` guard.
 *
 * @example
 * ```typescript
 * // Opened via a dialog service:
 * this._dialog.open(UpdatePasswordSubmitFormComponent);
 * ```
 *
 * @see UserRequestService
 * @see SetPasswordControlComponent
 * @see ISubmitFormDialog
 */
@Component({
    selector: "mgd-update-password-submit-form",
    templateUrl: "./update-password-submit-form.component.html",
    styleUrl: "./update-password-submit-form.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatButton,
        MatError,
        MatFormField,
        MatLabel,
        MatSuffix,
        MatInput,
        EnhancedFormDirective,
        SetPasswordControlComponent,
        DialogContentContainerDirective,
        DialogCloseButtonDirective,
        ButtonDirective,
        ErrorMessageComponent,
        EnhancedControlDirective,
        ClearInputDirective,
        ClearButtonComponent
    ]
})
export class UpdatePasswordSubmitFormComponent implements OnInit, AfterViewInit, ISubmitFormDialog {

    /**
     * Reference to the `EnhancedFormDirective` in the template.
     *
     * Required by {@link ISubmitFormDialog} / {@link ICheckChangesComponent} to support
     * the unsaved-changes guard. `canSubmit` is evaluated here before submitting.
     */
    @ViewChild(EnhancedFormDirective) form: EnhancedFormDirective;

    /**
     * When set to `true`, instructs the `checkChangesCanDeactivate` guard to skip
     * the dirty-state check. Set after a successful password update.
     */
    skipCheck?: boolean;

    /**
     * The reactive form group containing `currentPassword` and `newPassword` controls.
     */
    readonly updatePasswordForm = new FormGroup<IUpdatePasswordFormGroup>({
        currentPassword: new FormControl<string>("", {
            nonNullable: true,
            validators: [Validators.required]
        }),
        newPassword: new FormControl<string>({
            value: "",
            disabled: true
        }, {
            nonNullable: true,
            validators: [Validators.required]
        })
    });

    /**
     * Returns the `newPassword` `FormControl`.
     *
     * Used internally to enable/disable the control based on the `currentPassword` value,
     * and to retrieve the value when submitting.
     */
    get newPasswordControl(): FormControl<string> {

        return this.updatePasswordForm.controls.newPassword;
    }

    private readonly _dialogRef = inject(MatDialogRef<UpdatePasswordSubmitFormComponent>);
    private readonly _userService = inject(UserRequestService);
    private readonly _app = inject(COMMON_APP_SERVICE) as IAppService<ICommonConfig, IAuthenticationAppRouterService>;
    private readonly _destroyRef = inject(DestroyRef);

    /**
     * Subscribes to `currentPassword` value changes to enable or disable the `newPassword` control.
     *
     * - When `currentPassword` has a non-empty value, `newPassword` is enabled.
     * - When `currentPassword` is empty, `newPassword` is disabled.
     */
    ngOnInit(): void {

        this.updatePasswordForm.controls.currentPassword.valueChanges
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((value) => this._updateNewPasswordDisabledState(value));
    }

    /**
     * Re-asserts the `newPassword` control's disabled state against whatever value
     * `currentPassword` actually holds once the view has fully settled.
     *
     * The `currentPassword.valueChanges` subscription set up in {@link ngOnInit} only reacts to
     * future emissions — it does nothing for a value the control already holds at the moment
     * the subscription is wired up. `currentPassword` renders `autocomplete="off"`, the
     * `EnhancedControlDirective` default (this template does not set
     * `allowBrowserAutocomplete`), but browsers are documented
     * to disregard `autocomplete="off"` for password-manager autofill on password fields — so
     * autofill can still populate `currentPassword` before (or in the same tick as) that
     * subscription starts listening, in which case the subscription alone would never enable
     * `newPassword` to match. Reading `currentPassword.value` directly here re-syncs
     * `newPassword`'s disabled state to the control's actual current value, closing that timing
     * gap.
     *
     * Note: this no longer guards against `EnhancedFormDirective` force-enabling the form on
     * init — that historical workaround was removed; see {@link EnhancedFormDirective} for its
     * current disable/enable contract.
     */
    ngAfterViewInit(): void {

        this._updateNewPasswordDisabledState(this.updatePasswordForm.controls.currentPassword.value);
    }

    /**
     * Handles the submit button click.
     *
     * Validates the form via `form.canSubmit`, then calls `UserRequestService.setPassword$`.
     * The pipe uses `RxjsUtil.appProcessingUntilDone` to show a processing state while the
     * request is in flight.
     *
     * - **Success**: closes the dialog and displays a success notification.
     * - **BadRequestException**: sets validators on `currentPassword` so the inline error
     *   appears as soon as the form is re-enabled (via `formGroup.enable()` in the processing
     *   finalize step). The error auto-clears when the user changes the field value.
     * - **Other errors**: displays a generic error notification and escalates to the error handler.
     */
    onSubmitClick(): void {

        if (this.form?.canSubmit) {

            const newPassword = this.newPasswordControl.value;
            const currentPassword = this.updatePasswordForm.controls.currentPassword.value;

            this._userService.setPassword$(newPassword, currentPassword)
                .pipe(
                    RxjsUtil.appProcessingUntilDone(this._app),
                    takeUntilDestroyed(this._destroyRef)
                )
                .subscribe({
                    next: () => {

                        this.skipCheck = true;
                        this._dialogRef.close();
                        this._app.notificationService.displaySuccessMessage("Your password has been successfully updated.");
                    },
                    error: (error: unknown) => {

                        if (error instanceof BadRequestException) {

                            const ctrl = this.updatePasswordForm.controls.currentPassword;
                            ctrl.setValidators([Validators.required, CommonValidators.valueValidator(ctrl.value, "Current password is incorrect")]);
                            ctrl.markAsTouched();
                            ctrl.updateValueAndValidity();
                        }
                        else {
                            this._app.notificationService.displayErrorMessage("Failed to Update Password. Try again and contact support if this issue persists");
                            this._app.errorHandler.handleComponentError(error);
                        }
                    }
                });
        }
    }

    /**
     * Enables or disables the `newPassword` control based on whether `currentPassword` currently
     * has a value. Shared by the `currentPassword.valueChanges` subscription in {@link ngOnInit}
     * and the initial re-assertion in {@link ngAfterViewInit}.
     *
     * @param currentPasswordValue The current value of the `currentPassword` control.
     */
    private _updateNewPasswordDisabledState(currentPasswordValue: string): void {

        if (currentPasswordValue) {
            this.newPasswordControl.enable();
        }
        else {
            this.newPasswordControl.disable();
        }
    }
}
